<template>
    <article>

        <h2 id="rds">RDS</h2>

        <p>
            Each metric has the <var>rds_instance_id</var> label which includes the instance name and region, e.g., <var>us-west-1/pg-instance-1</var>.
        </p>

        <MetricDescription name="aws_rds_info" type="gauge" class="metric">
            <template #description>
                The RDS instance info.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/cli/latest/reference/rds/describe-db-instances.html" target="_blank" rel="noopener noreferrer">describe-db-instances</a>
            </template>
            <template #labels>
                <div><var>region</var> - the name of the region where the instance is located.</div>
                <div><var>availability_zone</var> - the name of the Availability Zone the instance is located in.</div>
                <div><var>endpoint</var> - the DNS address of the instance.</div>
                <div><var>ipv4</var> - the IP address of the instance.</div>
                <div><var>port</var> - the TCP port that the instance is listening on.</div>
                <div><var>engine</var> - the database engine.</div>
                <div><var>engine_version</var> - the database engine version.</div>
                <div><var>instance_type</var> -  the instance <a href="https://aws.amazon.com/rds/instance-types/" target="_blank" rel="noopener noreferrer">type</a> (compute and memory class name, e.g., <var>db.m5.large</var>) </div>
                <div><var>storage_type</var> - the <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/CHAP_Storage.html" target="_blank" rel="noopener noreferrer">storage type</a> of the instance.</div>
                <div><var>secondary_availability_zone</var> - the name of the secondary Availability zone for <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/Concepts.MultiAZ.html" target="_blank" rel="noopener noreferrer">Multi-AZ</a> deployments</div>
                <div><var>cluster_id</var> - if the instance is a part of an Aurora Cluster, this label contains the <var>id</var> of the cluster.</div>
                <div><var>source_instance_id</var> - if the instance is a <a href="https://aws.amazon.com/rds/features/read-replicas/" target="_blank" rel="noopener noreferrer">Read Replica</a>, this label contains the <var>id</var> of the primary instance.</div>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_status" type="gauge" class="metric">
            <template #description>
                Status of the RDS instance.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/cli/latest/reference/rds/describe-db-instances.html" target="_blank" rel="noopener noreferrer">describe-db-instances</a>
            </template>
            <template #labels>
                <var>status</var> - the <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/accessing-monitoring.html#Overview.DBInstance.Status" target="_blank" rel="noopener noreferrer">status</a> of the instance.
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_allocated_storage_gibibytes" type="gauge" class="metric">
            <template #source>
                <a href="https://docs.aws.amazon.com/cli/latest/reference/rds/describe-db-instances.html" target="_blank" rel="noopener noreferrer">describe-db-instances</a>
            </template>
            <template #description>
                Allocated storage size.
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_storage_autoscaling_threshold_gibibytes" type="gauge" class="metric">
            <template #source>
                <a href="https://docs.aws.amazon.com/cli/latest/reference/rds/describe-db-instances.html" target="_blank" rel="noopener noreferrer">describe-db-instances</a>
            </template>
            <template #description>
                Storage autoscaling threshold.
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_storage_provisioned_iops" type="gauge" class="metric">
            <template #source>
                <a href="https://docs.aws.amazon.com/cli/latest/reference/rds/describe-db-instances.html" target="_blank" rel="noopener noreferrer">describe-db-instances</a>
            </template>
            <template #description>
                Number of provisioned IOPs.
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_read_replica_info" type="gauge" class="metric">
            <template #description>
                Read replica info.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/cli/latest/reference/rds/describe-db-instances.html" target="_blank" rel="noopener noreferrer">describe-db-instances</a>
            </template>
            <template #labels>
                <var>replica_instance_id</var>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_backup_retention_period_days" type="gauge" class="metric">
            <template #description>
                Backup retention period.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/cli/latest/reference/rds/describe-db-instances.html" target="_blank" rel="noopener noreferrer">describe-db-instances</a>
            </template>
        </MetricDescription>


        <h2>OS metrics</h2>


        <MetricDescription name="aws_rds_cpu_cores" type="gauge" class="metric">
            <template #description>
                Number of virtual CPUs.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_cpu_usage_percent" type="gauge" class="metric">
            <template #description>
                Percentage of CPU spent in each mode.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>

            <template #labels>
                <var>mode</var>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_memory_total_bytes" type="gauge" class="metric">
            <template #description>
                Total amount of memory.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_memory_cached_bytes" type="gauge" class="metric">
            <template #description>
                Amount of memory used as page cache.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_memory_free_bytes" type="gauge" class="metric">
            <template #description>
                Amount of unassigned memory.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_net_(rx|tx)_bytes_per_second" type="gauge" class="metric">
            <template #description>
                Number of bytes received and transmitted per second.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>
            <template #labels>
                <var>interface</var>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_io_ops_per_second" type="gauge" class="metric">
            <template #description>
                Number of I/O transactions per second.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>

            <template #labels>
                <var>device</var>, <var>operation</var>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_io_bytes_per_second" type="gauge" class="metric">
            <template #description>
                Number of bytes read or written per second
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>

            <template #labels>
                <var>device</var>, <var>operation</var>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_io_await_seconds" type="gauge" class="metric">
            <template #description>
                Number of seconds required to respond to requests, including queue time and service time.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>

            <template #labels>
                <var>device</var>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_io_util_percent" type="gauge" class="metric">
            <template #description>
                Percentage of time that the disk spent serving I/O requests.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>

            <template #labels>
                <var>device</var>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_fs_total_bytes" type="gauge" class="metric">
            <template #description>
                Total amount of disk space available for the file system.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>

            <template #labels>
                <var>mount_point</var>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_rds_fs_used_bytes" type="gauge" class="metric">
            <template #description>
                Amount of disk space used by files in the file system.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_Monitoring.OS.overview.html" target="_blank" rel="noopener noreferrer">Enhanced Monitoring</a>
            </template>
            <template #labels>
                <var>mount_point</var>
            </template>
        </MetricDescription>

        <h2>Logs</h2>

        <MetricDescription name="aws_rds_log_messages_total" type="counter" class="metric">
            <template #description>
                Number of messages grouped by the automatically extracted repeated pattern.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/APIReference/API_DescribeDBLogFiles.html" target="_blank" rel="noopener noreferrer">DescribeDBLogFiles</a>,
                <a href="https://docs.aws.amazon.com/AmazonRDS/latest/APIReference/API_DownloadDBLogFilePortion.html" target="_blank" rel="noopener noreferrer">DownloadDBLogFilePortion</a>
            </template>
            <template #labels>
                <div><var>level</var> - <var>&lt;unknown | debug | info | warning | error | critical&gt;</var></div>
                <div><var>pattern_hash</var> - the ID of the automatically extracted repeated pattern</div>
                <div><var>sample</var> - a sample message of the group</div>
            </template>
        </MetricDescription>


        <h2 id="elasticache">Elasticache</h2>

        <p>
            Each metric has the <var>ec_instance_id</var> label which includes the cluster name, node id, and region, e.g., <var>us-west-1/redis-cluster-1/0001</var>.
        </p>

        <MetricDescription name="aws_elasticache_info" type="gauge" class="metric">
            <template #description>
                The Elasticache instance info.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/cli/latest/reference/elasticache/describe-cache-clusters.html#describe-cache-clusters" target="_blank" rel="noopener noreferrer">describe-cache-clusters</a>
            </template>
            <template #labels>
                <div><var>region</var> - the name of the region where the instance is located.</div>
                <div><var>availability_zone</var> - the name of the Availability Zone the instance is located in.</div>
                <div><var>endpoint</var> - the DNS address of the instance.</div>
                <div><var>ipv4</var> - the IP address of the instance.</div>
                <div><var>port</var> - the TCP port that the instance is listening on.</div>
                <div><var>engine</var> - the cache engine.</div>
                <div><var>engine_version</var> - the cache engine version.</div>
                <div><var>instance_type</var> -  the instance <a href="https://docs.aws.amazon.com/AmazonElastiCache/latest/red-ug/CacheNodes.SupportedTypes.html" target="_blank" rel="noopener noreferrer">type</a> (compute and memory class name, e.g., <var>cache.m6g.large</var>) </div>
                <div><var>cluster_id</var> - the <var>id</var> of the cluster.</div>
            </template>
        </MetricDescription>

        <MetricDescription name="aws_elasticache_status" type="gauge" class="metric">
            <template #description>
                Status of the Elasticache instance.
            </template>
            <template #source>
                <a href="https://docs.aws.amazon.com/cli/latest/reference/elasticache/describe-cache-clusters.html#describe-cache-clusters" target="_blank" rel="noopener noreferrer">describe-cache-clusters</a>
            </template>
            <template #labels>
                <var>status</var> - the <a href="https://docs.aws.amazon.com/AmazonElastiCache/latest/APIReference/API_CacheNode.html" target="_blank" rel="noopener noreferrer">status</a> of the instance.
            </template>
        </MetricDescription>

    </article>
</template>

<script>
import MetricDescription from "@/components/MetricDescription";

export default {
    components: {MetricDescription},
}
</script>

<style scoped>

.metric {
    margin: 36px 0;
}

</style>
